import React, { useRef } from 'react';
import { Box, useMantineColorScheme } from '@mantine/core';
import AceEditor from 'react-ace';
import ace from 'ace-builds';

import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/mode-html';
import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/mode-text';
import 'ace-builds/src-noconflict/mode-markdown';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';

import type { ScimAceStaticCompletion } from '../../utils/scimFilterEditorCompletions';

if (typeof window !== 'undefined' && ace.config) {
    const cdnBase = 'https://cdn.jsdelivr.net/npm/ace-builds@1.23.4/src-noconflict/';
    ace.config.set('basePath', cdnBase);
    ace.config.set('workerPath', cdnBase);
    ace.config.setModuleUrl('ace/mode/javascript_worker', `${cdnBase}worker-javascript.js`);
    ace.config.setModuleUrl('ace/mode/json_worker', `${cdnBase}worker-json.js`);
}

export type AceEditorMode = 'javascript' | 'json' | 'html' | 'sql' | 'markdown' | 'text';

export interface ScimAceEditorProps {
    mode: AceEditorMode;
    value: string;
    onChange?: (value: string) => void;
    readOnly?: boolean;
    /** Force theme (e.g. 'monokai'). When set, overrides colorScheme-based theme for consistent readability. */
    theme?: 'monokai' | 'github';
    height?: string;
    width?: string;
    fontSize?: number;
    showLineNumbers?: boolean;
    showGutter?: boolean;
    showPrintMargin?: boolean;
    highlightActiveLine?: boolean;
    enableAutocompletion?: boolean;
    enableSnippets?: boolean;
    enableLiveAutocompletion?: boolean;
    tabSize?: number;
    placeholder?: string;
    name?: string;
    onLoad?: (editor: any) => void | Promise<void>;
    setOptions?: Record<string, any>;
    className?: string;
    style?: React.CSSProperties;
    /**
     * When non-empty, registers a session completer (attributes + SCIM tokens) and enables basic/live completion.
     * List is read from a ref so updates apply without remounting the editor.
     */
    staticCompletions?: ScimAceStaticCompletion[] | null;
    /** Optional ref populated with the Ace editor instance after mount. */
    editorInstanceRef?: React.MutableRefObject<any | null>;
}

export const ScimAceEditor: React.FC<ScimAceEditorProps> = ({
    mode,
    value,
    onChange,
    readOnly = false,
    theme: themeProp,
    height = '400px',
    width = '100%',
    fontSize = 14,
    showLineNumbers = true,
    showGutter = true,
    showPrintMargin = false,
    highlightActiveLine = true,
    enableAutocompletion = false,
    enableSnippets = false,
    enableLiveAutocompletion = false,
    tabSize = 2,
    placeholder,
    name = 'scim-ace-editor',
    onLoad,
    setOptions = {},
    className,
    style,
    staticCompletions = null,
    editorInstanceRef,
}) => {
    const { colorScheme } = useMantineColorScheme();
    const editorRef = useRef<any>(null);
    const staticCompletionsRef = useRef(staticCompletions);
    staticCompletionsRef.current = staticCompletions;

    const aceTheme = themeProp ?? (colorScheme === 'dark' ? 'monokai' : 'github');

    const completionEnabled = enableAutocompletion || (staticCompletions != null && staticCompletions.length > 0);
    const liveCompletionEnabled = enableLiveAutocompletion || (staticCompletions != null && staticCompletions.length > 0);

    const defaultOptions: Record<string, any> = {
        showLineNumbers,
        tabSize,
        useWorker: false,
        foldStyle: 'markbeginend',
        showFoldWidgets: true,
        enableBasicAutocompletion: completionEnabled,
        enableLiveAutocompletion: liveCompletionEnabled,
        enableSnippets: enableSnippets,
        ...setOptions
    };

    const handleLoad = async (editor: any) => {
        editorRef.current = editor;
        if (editorInstanceRef) {
            editorInstanceRef.current = editor;
        }

        const list = staticCompletionsRef.current;
        if (list && list.length > 0) {
            const completer = {
                getCompletions(_ed: any, _session: any, _pos: any, prefix: string, callback: (err: null, results: unknown[]) => void) {
                    const current = staticCompletionsRef.current ?? [];
                    const p = (prefix || '').trim().toLowerCase();
                    const matches = current
                        .filter((c) => {
                            if (!p) return true;
                            const cap = c.caption.toLowerCase();
                            const val = (c.value ?? c.caption).toLowerCase();
                            return cap.includes(p) || val.includes(p);
                        })
                        .map((c) => ({
                            name: c.caption,
                            value: c.value ?? c.caption,
                            score: c.score ?? 50,
                            meta: c.meta ?? '',
                        }));
                    callback(null, matches);
                },
            };
            editor.completers = [completer];
        }

        if (onLoad) {
            await onLoad(editor);
        }
    };

    return (
        <Box className="scim-ace-editor-wrap" style={{ borderRadius: 4, overflow: 'hidden' }}>
            <AceEditor
                mode={mode}
                theme={aceTheme}
                value={value}
                onChange={onChange}
                readOnly={readOnly}
                name={name}
                editorProps={{ $blockScrolling: true }}
                width={width}
                height={height}
                fontSize={fontSize}
                showPrintMargin={showPrintMargin}
                showGutter={showGutter}
                highlightActiveLine={highlightActiveLine}
                setOptions={defaultOptions}
                onLoad={handleLoad}
                placeholder={placeholder}
                className={className}
                style={style}
            />
        </Box>
    );
};
